import type { AiDecision, AiModerationTrace, ArchiveRecord } from '../types/moderation';
import HelpHint from './HelpHint';

interface AiSidebarSummaryProps {
  record: ArchiveRecord;
}

const DECISION_LABEL: Record<AiDecision, string> = {
  auto_approved: 'AI: auto-approved',
  auto_rejected: 'AI: auto-rejected',
  flagged_for_human: 'AI: human review',
};

const DECISION_CLASS: Record<AiDecision, string> = {
  auto_approved: 'ai-summary__verdict ai-summary__verdict--approved',
  auto_rejected: 'ai-summary__verdict ai-summary__verdict--rejected',
  flagged_for_human: 'ai-summary__verdict ai-summary__verdict--flagged',
};

/**
 * F-105: confidence is a percentage; surface a band label so moderators
 * can calibrate quickly. >= 85 high, >= 60 medium, otherwise low.
 */
function confidenceBand(c: number): 'low' | 'medium' | 'high' {
  if (c >= 0.85) return 'high';
  if (c >= 0.6) return 'medium';
  return 'low';
}

/**
 * F-105: one-sentence verdict explainer derived from the signals. Reads
 * the AI decision and explains *why* in plain English so first-time
 * volunteers don't have to interpret three condensed chips on their own.
 */
function verdictExplainer(trace: AiModerationTrace): string {
  const reasons: string[] = [];
  if (trace.signals.safety.graphicSexual.detected) reasons.push('graphic sexual content');
  if (trace.signals.safety.graphicViolence.detected) reasons.push('graphic violence');
  if (trace.signals.safety.spam.detected) reasons.push('spam');
  if (trace.signals.safety.needsContext?.detected) reasons.push('needs more context');
  if (trace.signals.pii.detected) reasons.push(`third-party PII (${trace.signals.pii.spans.length})`);
  if (trace.signals.duplicate.isDuplicate) reasons.push('possible duplicate');
  switch (trace.decision) {
    case 'auto_approved':
      return 'Auto-approve recommended; no safety, PII, or duplicate signals fired.';
    case 'auto_rejected':
      return reasons.length > 0
        ? `Auto-reject recommended because ${reasons.join(' and ')}.`
        : 'Auto-reject recommended.';
    case 'flagged_for_human':
    default:
      return reasons.length > 0
        ? `Human review needed because ${reasons.join(' and ')}.`
        : 'Human review needed.';
  }
}

type SignalKind = 'safety' | 'pii' | 'duplicate';

interface SignalChip {
  label: string;
  tone: 'good' | 'warn';
  kind: SignalKind;
}

function buildSignalChips(trace: AiModerationTrace): SignalChip[] {
  const chips: SignalChip[] = [];
  const safety = trace.signals.safety;
  const anySafety =
    safety.graphicSexual.detected ||
    safety.graphicViolence.detected ||
    safety.spam.detected;
  // F-105: emoji prefixes vary in rendering across platforms; render an
  // inline SVG icon (warn or check) instead so screen-reader users and
  // sighted users both get a stable cue.
  chips.push({
    label: anySafety ? 'Safety flag' : 'Safe',
    tone: anySafety ? 'warn' : 'good',
    kind: 'safety',
  });
  chips.push({
    label: trace.signals.pii.detected
      ? `PII (${trace.signals.pii.spans.length})`
      : 'PII clean',
    tone: trace.signals.pii.detected ? 'warn' : 'good',
    kind: 'pii',
  });
  chips.push({
    label: trace.signals.duplicate.isDuplicate
      ? 'Possible duplicate'
      : 'Unique',
    tone: trace.signals.duplicate.isDuplicate ? 'warn' : 'good',
    kind: 'duplicate',
  });
  return chips;
}

function ChipIcon({ tone }: { tone: 'good' | 'warn' }) {
  // Inline SVGs avoid emoji rendering variance.
  return tone === 'good' ? (
    <svg
      className="ai-summary__chip-icon"
      width="11"
      height="11"
      viewBox="0 0 12 12"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 6.5l2.5 2.5L10 3" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg
      className="ai-summary__chip-icon"
      width="11"
      height="11"
      viewBox="0 0 12 12"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6 1l5 9.5H1L6 1z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
      <line x1="6" y1="5" x2="6" y2="7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="6" cy="9" r="0.6" fill="currentColor" />
    </svg>
  );
}

const CHIP_HINTS: Record<SignalKind, { topic: string; body: string; anchor: string }> = {
  safety: {
    topic: 'Safety signal',
    body: 'Combines graphic sexual, graphic violence, and spam checks. The archive context allows tasteful nudity, drag, and protest scenes; only genuinely graphic content or obvious slop trips this.',
    anchor: '#/guide#reading-the-ai-moderators-verdict',
  },
  pii: {
    topic: 'PII signal',
    body: 'Names, emails, phones, or addresses of third parties (not the submitter and not public figures). The AI flags them; the moderator decides whether to publish or redact.',
    anchor: '#/guide#sensitivity-and-privacy',
  },
  duplicate: {
    topic: 'Duplicate signal',
    body: 'Compares a perceptual hash of the image against existing records. A near-match suggests the same item was already submitted; cross-check before deciding.',
    anchor: '#/guide#reading-the-ai-moderators-verdict',
  },
};

export default function AiSidebarSummary({ record }: AiSidebarSummaryProps) {
  const traces = record.aiAnalysis || [];
  if (traces.length === 0) return null;
  const trace = traces[traces.length - 1];

  const chips = buildSignalChips(trace);
  const themes = trace.signals.metadata.themes || [];
  const decades = trace.signals.metadata.decades || [];
  const summary = trace.signals.metadata.summary;

  const handleViewReasoning = () => {
    // Switch to History tab on the parent SubmissionDetail. The tab API
    // surface here is a window event; SubmissionDetail listens for it.
    window.dispatchEvent(
      new CustomEvent('moderation:show-history', { detail: { traceId: trace.id } }),
    );
  };

  // F-105: derived legend + confidence band.
  const band = confidenceBand(trace.confidence);
  const explainer = verdictExplainer(trace);

  return (
    <div className="ai-summary" data-testid="ai-sidebar-summary">
      <div className={DECISION_CLASS[trace.decision]} data-testid="ai-verdict">
        {DECISION_LABEL[trace.decision]}
      </div>
      {/* F-105: one-sentence explainer beneath the verdict pill so
          first-timers don't have to interpret three chips in isolation. */}
      <p className="ai-summary__explainer" data-testid="ai-explainer">
        {explainer}
      </p>
      <div className="ai-summary__confidence" data-testid="ai-confidence">
        Confidence: {Math.round(trace.confidence * 100)}%{' '}
        <span
          className={`ai-summary__confidence-band ai-summary__confidence-band--${band}`}
          data-testid="ai-confidence-band"
        >
          ({band})
        </span>
      </div>
      <div className="ai-summary__chip-label" data-testid="ai-signals-label">
        Signals checked:
      </div>
      <div className="ai-summary__chips" data-testid="ai-signals">
        {chips.map((c, i) => {
          const hint = CHIP_HINTS[c.kind];
          return (
            <span
              key={i}
              className={`ai-summary__chip ai-summary__chip--${c.tone}`}
            >
              <ChipIcon tone={c.tone} />
              {c.label}
              <HelpHint topic={hint.topic} className="ai-summary__chip-hint">
                <p>{hint.body}</p>
                <a href={hint.anchor}>Read more →</a>
              </HelpHint>
            </span>
          );
        })}
      </div>
      {summary && (
        <p className="ai-summary__line" data-testid="ai-summary-line">
          {summary}
        </p>
      )}
      {(themes.length > 0 || decades.length > 0) && (
        <div className="ai-summary__suggestions" data-testid="ai-suggestions">
          <div className="ai-summary__suggestion-label">Suggested:</div>
          <div className="ai-summary__suggestion-chips">
            {themes.slice(0, 3).map((t) => (
              <span key={`t-${t}`} className="ai-summary__suggestion-chip">
                {t}
              </span>
            ))}
            {decades.slice(0, 2).map((d) => (
              <span key={`d-${d}`} className="ai-summary__suggestion-chip ai-summary__suggestion-chip--decade">
                {d}
              </span>
            ))}
          </div>
        </div>
      )}
      <button
        type="button"
        className="ai-summary__reasoning-link"
        onClick={handleViewReasoning}
        data-testid="ai-view-reasoning"
      >
        View reasoning →
      </button>
    </div>
  );
}
